import { FormEvent, useState } from 'react';
import { Box, Button, TextField, Typography } from '@material-ui/core';
import { CopyTextButton, Link, WarningPanel } from '@backstage/core-components';
import { configApiRef, useApi } from '@backstage/core-plugin-api';
import { usePermission } from '@backstage/plugin-permission-react';
import {
  testJwtCreatePermission,
  testJwtLookupPermission,
} from '@internal/plugin-test-jwt-common';
import { usePost } from './usePost';

type MintedToken = { value: string; expiresAt: Date };

/**
 * Compact "Get test JWT" form shown above Swagger UI. Renders nothing for
 * users without test-jwt.create.
 */
export const TestJwtMintControl = ({
  entityRef,
  onToken,
}: {
  entityRef: string;
  onToken: (token: string) => void;
}) => {
  const { allowed } = usePermission({ permission: testJwtCreatePermission });
  const { allowed: canLookup } = usePermission({
    permission: testJwtLookupPermission,
  });
  const post = usePost();
  const config = useApi(configApiRef);
  const [tenantId, setTenantId] = useState(
    () => config.getOptionalString('testJwt.defaults.tenantId') ?? '',
  );
  const [userId, setUserId] = useState(
    () => config.getOptionalString('testJwt.defaults.userId') ?? '',
  );
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>();
  // Kept in component state only; never persisted.
  const [token, setToken] = useState<MintedToken>();

  if (!allowed) return null;

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(undefined);
    try {
      const res = await post<{ access_token: string; expires_in: number }>(
        '/token',
        {
          entityRef,
          ...(tenantId && { tenantId }),
          ...(userId && { userId }),
        },
      );
      setToken({
        value: res.access_token,
        expiresAt: new Date(Date.now() + res.expires_in * 1000),
      });
      onToken(res.access_token);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setLoading(false);
    }
  };

  return (
    // Right-aligned, on the same side as Swagger UI's Authorize button.
    <Box
      mb={2}
      display="flex"
      flexDirection="column"
      alignItems="flex-end"
      textAlign="right"
    >
      <Typography variant="h6">Get test JWT (eu-dev)</Typography>
      <form onSubmit={onSubmit}>
        <Box display="flex" alignItems="center" style={{ gap: 16 }}>
          <TextField
            id="test-jwt-tenant-id"
            label="Tenant ID"
            value={tenantId}
            onChange={e => setTenantId(e.target.value)}
            size="small"
          />
          <TextField
            id="test-jwt-user-id"
            label="User ID"
            value={userId}
            onChange={e => setUserId(e.target.value)}
            size="small"
          />
          <Button
            type="submit"
            variant="contained"
            color="primary"
            disabled={loading}
          >
            Create
          </Button>
          {canLookup && (
            <Link to="/test-jwt/lookup">Who requested a token?</Link>
          )}
        </Box>
      </form>
      <Typography variant="caption" color="textSecondary">
        Both optional. Most platform APIs need a tenant; User ID requires Tenant
        ID.
      </Typography>
      {error && (
        <WarningPanel severity="error" title="Could not create token">
          {error}
        </WarningPanel>
      )}
      {token && (
        <Box display="flex" alignItems="center" mt={1}>
          <Typography variant="body2">
            Token applied to "Try it out". Expires{' '}
            {token.expiresAt.toLocaleString()}.
          </Typography>
          <CopyTextButton text={token.value} />
        </Box>
      )}
    </Box>
  );
};
